import {
    CLEAN_LOCATIONS,
    CLOSE_SESSION,
    LOAD_DEVICES_COMPLETE,
    REMOVE_TICKET,
    SET_DEVICES_LOADING,
    UPDATE_CUSTOMER_LABELS,
    UPDATE_DEVICE,
    UPDATE_DEVICE_LOCATIONS,
    UPDATE_TICKET,
    UPDATE_WEIGHTS,
    LOAD_CARTSLOCATIONS_COMPLETE,
    UPDATE_CART_LOCATION,
} from './sessionMonitor.action';
import { CartLocationMap } from '@/carts/gps/cartGpsUtils';
import { Config } from '@/config/configType';
import type { CustomerLabel } from '@whiz-cart/node-shared/models/customer/customerLabel';
import { SessionActiveDevice } from '@whiz-cart/node-shared/models/session/activeDevice';
import { CustomerTicket, StoreCustomerTicket } from '@whiz-cart/node-shared/models/ticket/ticket';

export interface SessionMonitorState {
    hasLoadedOnce: boolean;
    isLoading: boolean;
    devices: SessionActiveDevice[];
    cartsLocations?: CartLocationMap[];
}

const isTicketActive = (t?: CustomerTicket) => t && !t.closedOn;

const updateSession = (
    devices: SessionActiveDevice[],
    find: string | ((d: SessionActiveDevice) => unknown),
    update: (d?: SessionActiveDevice) => SessionActiveDevice,
    upsert?: boolean,
) => {
    const _find = find instanceof Function ? find : (d: SessionActiveDevice) => d.deviceId === find;
    const _update = update instanceof Function ? update : () => update;
    let found;

    devices = devices.map((d) => {
        if (!_find(d)) return d;
        found = true;
        return _update(d);
    });

    if (!found && upsert) devices.push(_update());
    return devices;
};

const updateTicket = (devices: SessionActiveDevice[], id: string, update?: StoreCustomerTicket, upsert?: boolean) => {
    return updateSession(
        devices,
        (d) => d.tickets?.find((t) => t.id === id) || d.deviceId === update?.deviceId,
        (d) => {
            let found = false;
            let updatedTicket: StoreCustomerTicket | undefined = undefined;
            const tickets = (d?.tickets || []).map((t) => {
                if (t.id !== id) return t;
                found = true;
                return (updatedTicket = update);
            });

            if (!found && upsert) tickets.push((updatedTicket = update));
            return { deviceId: updatedTicket?.deviceId, ...d, tickets: tickets.filter(isTicketActive) as StoreCustomerTicket[] };
        },
        upsert,
    );
};

const updateWeights = (
    devices: (SessionActiveDevice & {
        weightUpdatedOn?: Date;
    })[],
    {
        sessionGuid,
        actualWeight,
        expectedWeight,
        t,
    }: {
        sessionGuid: string;
        actualWeight: number;
        expectedWeight: number;
        t: Date;
    },
) => {
    return devices.map((device) => {
        if (device.sessionGuid !== sessionGuid) return device;
        if (new Date(device.weightUpdatedOn ?? 0).getTime() > new Date(t).getTime()) return device;

        return {
            ...device,
            actualWeight: actualWeight ?? device.actualWeight,
            expectedWeight: expectedWeight ?? device.expectedWeight,
            weightUpdatedOn: t,
        };
    });
};

type SessionMonitorReducerParam =
    | {
          type: typeof SET_DEVICES_LOADING;
          payload: boolean;
      }
    | {
          type: typeof LOAD_DEVICES_COMPLETE;
          payload: SessionActiveDevice[];
      }
    | {
          type: typeof UPDATE_DEVICE;
          payload: SessionActiveDevice;
      }
    | {
          type: typeof CLOSE_SESSION;
          payload: {
              sessionGuid: string;
          };
      }
    | {
          type: typeof UPDATE_TICKET;
          payload: StoreCustomerTicket;
      }
    | {
          type: typeof REMOVE_TICKET;
          payload: string;
      }
    | {
          type: typeof UPDATE_DEVICE_LOCATIONS;
          payload: SessionActiveDevice[];
      }
    | {
          type: typeof CLEAN_LOCATIONS;
          payload: undefined;
      }
    | {
          type: typeof UPDATE_WEIGHTS;
          payload: {
              sessionGuid: string;
              actualWeight: number;
              expectedWeight: number;
              t: Date;
          };
      }
    | {
          type: typeof UPDATE_CUSTOMER_LABELS;
          payload: {
              customerGuid: string;
              labels: CustomerLabel[];
          };
      }
    | {
          type: typeof LOAD_CARTSLOCATIONS_COMPLETE;
          payload: CartLocationMap[];
      }
    | {
          type: typeof UPDATE_CART_LOCATION;
          payload: {
              storeGuid: string;
              deviceId: string;
              location: {
                  lat: number;
                  lon: number;
                  createdOn: Date;
              };
          };
      };

export default (
    state: SessionMonitorState = {
        hasLoadedOnce: false,
        isLoading: false,
        devices: [],
    },
    { type, payload }: SessionMonitorReducerParam,
    { config }: { config?: Config } = {},
) => {
    switch (type) {
        case SET_DEVICES_LOADING:
            return { ...state, isLoading: payload };

        case LOAD_DEVICES_COMPLETE:
            return {
                ...state,
                hasLoadedOnce: true,
                isLoading: false,
                devices: payload,
            };

        case UPDATE_DEVICE:
            return {
                ...state,
                devices: updateSession(
                    state.devices.filter((d) => d.deviceId === payload.deviceId || d.sessionGuid !== payload.sessionGuid),
                    payload.deviceId ?? '',
                    (d) => ({ ...d, ...payload }),
                    true,
                ),
            };

        case LOAD_CARTSLOCATIONS_COMPLETE:
            return {
                ...state,
                cartsLocations: payload,
            };

        case UPDATE_CART_LOCATION:
            return {
                ...state,
                cartsLocations: state?.cartsLocations?.find((c) => c?.cartId === payload?.deviceId)
                    ? state.cartsLocations?.map((cartLocation) => {
                          if (cartLocation.cartId !== payload.deviceId) {
                              return cartLocation;
                          }

                          return {
                              ...cartLocation,
                              lat: payload.location.lat,
                              lon: payload.location.lon,
                              lng: payload.location.lon,
                              createdOn: payload.location.createdOn,
                          };
                      })
                    : [
                          ...(state?.cartsLocations ?? []),
                          {
                              storeGuid: payload.storeGuid,
                              cartGuid: payload.deviceId,
                              deviceId: payload.deviceId,
                              cartId: payload.deviceId,
                              lat: payload.location.lat,
                              lon: payload.location.lon,
                              lng: payload.location.lon,
                              createdOn: payload.location.createdOn,
                          },
                      ],
            };

        case CLOSE_SESSION:
            return {
                ...state,
                devices: updateSession(
                    state.devices,
                    (d) => d.sessionGuid === payload.sessionGuid,
                    (d) => ({ ...d, sessionGuid: undefined }),
                ),
            };

        case UPDATE_TICKET:
            return {
                ...state,
                devices: updateTicket(state.devices, payload.id ?? '', payload, true),
            };

        case REMOVE_TICKET:
            return {
                ...state,
                devices: updateTicket(state.devices, payload),
            };

        case UPDATE_DEVICE_LOCATIONS:
            return {
                ...state,
                devices: payload.reduce(
                    (devices, { deviceId, location }) =>
                        updateSession(devices, deviceId ?? '', (d) => ({ ...d, deviceId, location }), true),
                    state.devices,
                ),
            };

        case CLEAN_LOCATIONS:
            return {
                ...state,
                devices: state.devices.map((device) => ({
                    ...device,
                    location:
                        new Date(device.location?.ts ?? 0).getTime() > new Date().getTime() - (config?.locationsStaleAfter ?? 0)
                            ? device.location
                            : null,
                })),
            };

        case UPDATE_WEIGHTS:
            return {
                ...state,
                devices: updateWeights(state.devices, payload),
            };

        case UPDATE_CUSTOMER_LABELS: {
            const labels = payload.labels.filter((label) => !label.deletedOn).map((label) => label.label);

            return {
                ...state,
                devices: state.devices.map((device) => {
                    if (device.customer?.userGuid !== payload.customerGuid) {
                        return device;
                    }

                    return {
                        ...device,
                        customer: {
                            ...device.customer,
                            labels: labels,
                        },
                    };
                }),
            };
        }

        default:
            return state;
    }
};
